import { useId, type ComponentProps } from "react";

/**
 * The Lumos wordmark (white letters, glowing "O"), inline rather than an <img>
 * so it needs no asset URL and its blur filters
 * render. Filter ids come from useId: the rail mounts the wordmark and the
 * collapsed mark side by side, and duplicate ids would make one borrow the
 * other's filter.
 *
 * `mark` crops to the glowing "O" alone, for the collapsed 52px rail.
 */
export function LumosLogo({ mark = false, ...props }: ComponentProps<"svg"> & { mark?: boolean }) {
  const id = useId().replace(/:/g, "");
  const outer = `lumos-outer-${id}`;
  const inner = `lumos-inner-${id}`;

  return (
    <svg
      viewBox={mark ? "552 2 274 270" : "0 0 972 274"}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Lumos"
      {...props}
    >
      {!mark && (
        <>
          <path
            d="M395.565 38.3647L452.904 157.886L511.416 38.3647H554.714V235.189H519.926L521.918 89.8999L462.281 209.421H445.778L381.523 89.8999L383.844 235.189H350.908L351.048 38.3647H395.518H395.565Z"
            fill="white"
          />
          <path
            d="M771.931 70.2989C814.971 119.722 797.483 204.112 736.276 229.856C662.363 260.951 577.456 213.03 583.761 128.71C590.794 34.6513 712.388 1.93731 771.931 70.2989ZM742.066 86.1161C707.724 48.9197 632.802 63.6106 621.738 117.516C598.999 228.237 757.304 238.07 760.187 139.529C760.726 121.107 754.702 99.7978 742.066 86.1396V86.1161Z"
            fill="white"
          />
          <path
            d="M966.638 78.1657L940.055 89.5475C928.638 75.4904 912.276 63.6157 893.006 64.085C857.773 64.9299 843.379 98.9816 883.465 113.461C915.229 124.937 964.106 125.406 970.74 168.986C983.704 254.033 842.23 258.867 816.89 189.497C816.983 188.089 841.973 176.941 841.973 176.941C849.92 189.497 858.847 195.17 867.548 199.658C887.698 210.053 930.936 212.8 933.936 184.85C936.304 162.72 923.27 158.402 905.29 152.512C867.267 140.05 817.757 139.886 824.157 84.3142C831.611 19.4259 947.954 20.7636 966.661 78.1657H966.638Z"
            fill="white"
          />
          <path
            d="M182.57 38.3647L184.82 168.493C184.305 205.549 228.118 216.626 254.959 198.696C263.562 192.947 271.509 178.444 271.509 168.4V38.3647H306.626V173.093C306.626 258.727 149.798 256.169 149.798 177.787V38.3647H182.57Z"
            fill="white"
          />
          <path d="M35.1163 38.3647V202.381H119.367V235.189H0V38.3647H35.1163Z" fill="white" />
        </>
      )}
      {mark && (
        <path
          d="M771.931 70.2989C814.971 119.722 797.483 204.112 736.276 229.856C662.363 260.951 577.456 213.03 583.761 128.71C590.794 34.6513 712.388 1.93731 771.931 70.2989ZM742.066 86.1161C707.724 48.9197 632.802 63.6106 621.738 117.516C598.999 228.237 757.304 238.07 760.187 139.529C760.726 121.107 754.702 99.7978 742.066 86.1396V86.1161Z"
          fill="white"
        />
      )}
      <g filter={`url(#${outer})`}>
        <ellipse
          cx="89.1106"
          cy="88.2635"
          rx="89.1106"
          ry="88.2635"
          transform="matrix(0.870112 0.492853 -0.507181 0.86184 656.796 16.6572)"
          stroke="#008CFF"
          strokeWidth="55.2331"
        />
      </g>
      <g filter={`url(#${inner})`}>
        <ellipse cx="689.563" cy="136.645" rx="89.5311" ry="87.8369" stroke="white" strokeWidth="31.5617" />
      </g>
      <defs>
        <filter id={outer} x="556.413" y="5.59754" width="266.307" height="262.094" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="7.89044" result="effect1_foregroundBlur" />
        </filter>
        <filter id={inner} x="572.009" y="20.785" width="235.109" height="231.72" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="6.12119" result="effect1_foregroundBlur" />
        </filter>
      </defs>
    </svg>
  );
}
